/**
 * Family-specific engagement detail.
 *
 * Every field on this screen is declared by the selected family's rules and
 * rendered generically — there is no per-family JSX. A CoE proposal has no
 * fields and never shows this step at all; a managed-service proposal shows a
 * support tier; a solution build shows an entry point and solution types.
 *
 * Nothing here is printed on the proposal. The approved templates have no
 * section for engagement shape, and inventing one would break the formatting
 * fidelity the engine guarantees.
 */
import type {
  EngagementField,
  EngagementValue,
  FamilyRules,
  ProposalConfiguration,
} from '../../lib/api';
import { Card, Field, Input, Select } from '../../components/ui';

const OTHER = '__other__';

export function EngagementStep({
  config,
  update,
  disabled,
  flagged,
  rules,
}: {
  config: ProposalConfiguration;
  update<K extends keyof ProposalConfiguration>(key: K, value: ProposalConfiguration[K]): void;
  disabled: boolean;
  flagged: Set<string>;
  rules: FamilyRules;
}) {
  const engagement = config.engagement ?? {};

  const set = (key: string, value: EngagementValue) =>
    update('engagement', { ...engagement, [key]: value });

  return (
    <div className="space-y-6">
      {rules.sections.map((section) => (
        <Card key={section.id} title={section.title} description={section.description}>
          <div className="space-y-5">
            {section.fields.map((field) => (
              <EngagementFieldInput
                key={field.key}
                field={field}
                value={engagement[field.key]}
                onChange={(value) => set(field.key, value)}
                disabled={disabled}
                invalid={flagged.has(`engagement.${field.key}`)}
              />
            ))}
          </div>
        </Card>
      ))}

      <p className="rounded-md bg-tint px-3 py-2 text-[12px] text-muted">
        These answers shape scoping and delivery planning. They are stored on the proposal
        and are not printed in the Word document.
      </p>
    </div>
  );
}

function EngagementFieldInput({
  field,
  value,
  onChange,
  disabled,
  invalid,
}: {
  field: EngagementField;
  value: EngagementValue | undefined;
  onChange(value: EngagementValue): void;
  disabled: boolean;
  invalid: boolean;
}) {
  const error = invalid ? 'Required' : undefined;

  if (field.type === 'INTEGER') {
    return (
      <Field label={field.label} htmlFor={field.key} required={field.required} hint={field.helpText} error={error}>
        <Input
          id={field.key}
          type="number"
          inputMode="numeric"
          min={field.min}
          max={field.max}
          disabled={disabled}
          aria-invalid={invalid}
          className={invalid ? 'border-red-400' : undefined}
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) =>
            onChange(event.target.value === '' ? null : Number(event.target.value))
          }
        />
      </Field>
    );
  }

  if (field.type === 'TEXT') {
    return (
      <Field label={field.label} htmlFor={field.key} required={field.required} hint={field.helpText} error={error}>
        <Input
          id={field.key}
          disabled={disabled}
          maxLength={200}
          placeholder={field.placeholder}
          aria-invalid={invalid}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
        />
      </Field>
    );
  }

  if (field.type === 'BOOLEAN') {
    return (
      <label className="flex items-start gap-2.5">
        <input
          type="checkbox"
          disabled={disabled}
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
          className="mt-0.5 h-4 w-4 accent-teal"
        />
        <span>
          <span className="block text-[13px] font-medium text-ink">{field.label}</span>
          {field.helpText && (
            <span className="block text-[12px] text-muted">{field.helpText}</span>
          )}
        </span>
      </label>
    );
  }

  if (field.type === 'SINGLE_SELECT') {
    const options = field.options ?? [];
    // A short list with explanatory text reads far better as cards than as a
    // dropdown — the approved entry-point descriptions are the whole point.
    const asCards = options.length <= 4 && options.some((option) => option.description);

    if (asCards) {
      return (
        <fieldset>
          <legend className="mb-2 text-[13px] font-medium text-ink">
            {field.label}
            {field.required && <span className="ml-1 text-teal">*</span>}
          </legend>
          {field.helpText && <p className="mb-2 text-[12px] text-muted">{field.helpText}</p>}
          <div className="grid gap-2 sm:grid-cols-2">
            {options.map((option) => {
              const selected = value === option.value;
              return (
                <label
                  key={option.value}
                  className={[
                    'flex cursor-pointer gap-2.5 rounded-lg border p-3 transition-colors',
                    selected
                      ? 'border-teal bg-tint ring-1 ring-teal'
                      : 'border-rule hover:border-muted hover:bg-tint',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name={field.key}
                    value={option.value}
                    checked={selected}
                    disabled={disabled}
                    onChange={() => onChange(option.value)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-teal"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-ink">{option.label}</span>
                    {option.description && (
                      <span className="mt-0.5 block text-[12px] text-body">
                        {option.description}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          {error && (
            <p role="alert" className="mt-1.5 text-[12px] text-red-700">
              {error}
            </p>
          )}
        </fieldset>
      );
    }

    return (
      <Field label={field.label} htmlFor={field.key} required={field.required} hint={field.helpText} error={error}>
        <Select
          id={field.key}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value || null)}
        >
          <option value="">Select…</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>
    );
  }

  // MULTI_SELECT
  const selected = Array.isArray(value) ? value : [];
  const options = field.options ?? [];
  const known = new Set(options.map((option) => option.value));
  const otherValue = selected.find((entry) => !known.has(entry) && entry !== OTHER) ?? '';
  const otherEnabled = selected.includes(OTHER) || otherValue !== '';

  const toggle = (optionValue: string, on: boolean) => {
    const next = on
      ? [...selected, optionValue]
      : selected.filter((entry) => entry !== optionValue);
    // Preserve the declared option order so the stored answer is stable.
    onChange([
      ...options.map((o) => o.value).filter((v) => next.includes(v)),
      ...next.filter((v) => !known.has(v)),
    ]);
  };

  const setOther = (text: string) => {
    const withoutOther = selected.filter((entry) => known.has(entry));
    onChange(text.trim() ? [...withoutOther, text] : [...withoutOther, OTHER]);
  };

  return (
    <fieldset>
      <legend className="mb-2 text-[13px] font-medium text-ink">
        {field.label}
        {field.required && <span className="ml-1 text-teal">*</span>}
      </legend>
      {field.helpText && <p className="mb-2 text-[12px] text-muted">{field.helpText}</p>}

      <div className="grid gap-1.5 sm:grid-cols-2">
        {options.map((option) => (
          <label key={option.value} className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              disabled={disabled}
              onChange={(event) => toggle(option.value, event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-teal"
            />
            <span>
              <span className="text-ink">{option.label}</span>
              {option.mapsToScopeItem && (
                <span
                  className="block text-[11px] text-muted"
                  title="The approved scope item this maps to"
                >
                  {option.mapsToScopeItem}
                </span>
              )}
            </span>
          </label>
        ))}

        {field.allowOther && (
          <label className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={otherEnabled}
              disabled={disabled}
              onChange={(event) =>
                event.target.checked
                  ? setOther('')
                  : onChange(selected.filter((entry) => known.has(entry)))
              }
              className="mt-0.5 h-4 w-4 shrink-0 accent-teal"
            />
            <span className="w-full">
              <span className="text-ink">Other</span>
              {otherEnabled && (
                <Input
                  aria-label={`Other ${field.label}`}
                  className="mt-1"
                  maxLength={200}
                  disabled={disabled}
                  value={otherValue}
                  placeholder="Describe"
                  onChange={(event) => setOther(event.target.value)}
                />
              )}
            </span>
          </label>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-red-700">
          {error}
        </p>
      )}
    </fieldset>
  );
}
